import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { NotificationDto } from "../../services/requests/types.gen";

interface NotificationState {
  notifications: NotificationDto[];
  unreadCount: number;
  totalPages: number;
  currentPage: number;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
}

const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
  totalPages: 0,
  currentPage: 0,
  isLoading: false,
  isLoadingMore: false,
  error: null,
};

const notificationSlice = createSlice({
  name: "notification",
  initialState,
  reducers: {
    setNotifications: (
      state,
      action: PayloadAction<{
        notifications: NotificationDto[];
        totalElements: number;
        totalPages: number;
      }>,
    ) => {
      state.notifications = action.payload.notifications;
      state.unreadCount = action.payload.totalElements;
      state.totalPages = action.payload.totalPages;
      state.currentPage = 0;
    },
    appendNotifications: (
      state,
      action: PayloadAction<{ notifications: NotificationDto[]; page: number }>,
    ) => {
      state.notifications = [
        ...state.notifications,
        ...action.payload.notifications,
      ];
      state.currentPage = action.payload.page;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setLoadingMore: (state, action: PayloadAction<boolean>) => {
      state.isLoadingMore = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    markAsRead: (state, action: PayloadAction<string>) => {
      const notification = state.notifications.find(
        (n) => n.uuid === action.payload,
      );
      if (notification && !notification.isRead) {
        notification.isRead = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
    markAllAsRead: (state) => {
      state.notifications.forEach((notification) => {
        if (!notification.isRead) {
          notification.isRead = true;
        }
      });
      state.unreadCount = 0;
    },
    clearNotifications: (state) => {
      state.notifications = [];
      state.unreadCount = 0;
      state.totalPages = 0;
      state.currentPage = 0;
    },
  },
});

export const {
  setNotifications,
  appendNotifications,
  setLoading,
  setLoadingMore,
  setError,
  markAsRead,
  markAllAsRead,
  clearNotifications,
} = notificationSlice.actions;

export default notificationSlice.reducer;
